import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowRight } from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildBreadcrumbListJsonLd } from "../../seo/site";
import { trackEvent } from "../../utils/analytics";

const SEO_DESCRIPTION =
  "Tarifs Raguia : Starter à 90€/mois, Business à 150€/mois, Pro à 320€/mois. Chatbot IA sur vos documents. Setup fixe, hébergement France, RGPD. Démo gratuite.";

const PRICING_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Raguia — Assistant documentaire IA B2B",
  description:
    "Chatbot RAG sur vos documents internes. Hébergé en France, conforme RGPD, clé en main en 24h.",
  brand: { "@type": "Brand", name: "Raguia" },
  offers: [
    {
      "@type": "Offer",
      name: "Starter",
      price: "90",
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "90",
        priceCurrency: "EUR",
        unitCode: "MON",
      },
      description:
        "TPE, 1 équipe, 5 utilisateurs chatbot, 500 Mo de documents.",
      availability: "https://schema.org/InStock",
      url: "https://raguia.valentin-fiess.fr/tarifs",
    },
    {
      "@type": "Offer",
      name: "Business",
      price: "150",
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "150",
        priceCurrency: "EUR",
        unitCode: "MON",
      },
      description:
        "PME, 3 équipes, 20 utilisateurs chatbot, 3 Go de documents.",
      availability: "https://schema.org/InStock",
      url: "https://raguia.valentin-fiess.fr/tarifs",
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "320",
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "320",
        priceCurrency: "EUR",
        unitCode: "MON",
      },
      description:
        "PME/ETI, équipes illimitées, 50 utilisateurs chatbot, 15 Go de documents.",
      availability: "https://schema.org/InStock",
      url: "https://raguia.valentin-fiess.fr/tarifs",
    },
  ],
};

export default function PricingPage() {
  return (
    <>
      <SeoHead
        title="Tarifs Raguia — Chatbot IA sur vos documents | Starter 90€/mois"
        description={SEO_DESCRIPTION}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Tarifs", path: "/tarifs" },
          ]),
          PRICING_JSON_LD,
        ]}
      />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <p className="text-sm font-semibold uppercase tracking-wide text-[#A43032]">
          Tarification
        </p>
        <h1 className="font-heading mt-2 text-4xl font-semibold tracking-tight text-[#010101] sm:text-5xl">
          💼 Des offres claires, sans surprise
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-stone-800">
          Setup fixe + abonnement fixe, quelle que soit votre utilisation.
          Transparence budgétaire pour vos équipes finance et IT.
        </p>

        <section className="mt-14 space-y-10">
          <h2 className="font-heading text-2xl font-semibold text-[#010101]">
            🎯 Points à mettre en avant
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                title: "🔐 Sécurité",
                body: "Vos documents ne quittent pas mon serveur. Chatbot et portail durcis, chiffrement en transit et backups. Chaque client isolé : zéro mélange entre entreprises ou sous-clients.",
              },
              {
                title: "🇪🇺 Français / européen (RGPD)",
                body: "OVH (France). Mistral pour le chatbot — entreprise française, données en France — pour vos exigences de conformité.",
              },
              {
                title: "🚀 Clé en main",
                body: "Rien à installer ni maintenir : je m’occupe de tout.",
              },
              {
                title: "🎨 Adaptation au secteur",
                body: "Pas d’outil générique : l’assistant est configuré pour votre métier.",
              },
            ].map((card) => (
              <article
                key={card.title}
                className="rounded-2xl border-2 border-[#010101]/10 bg-white p-6 shadow-sm transition hover:border-[#A43032]/30 hover:shadow-md"
              >
                <h3 className="font-heading text-lg font-semibold text-[#010101]">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-800">
                  {card.body}
                </p>
              </article>
            ))}
            <article className="rounded-2xl border-2 border-[#A43032]/35 bg-gradient-to-br from-[#010101] to-stone-900 p-6 text-white shadow-lg md:col-span-2">
              <h3 className="font-heading text-lg font-semibold">💶 Pricing prévisible</h3>
              <p className="mt-2 text-sm leading-relaxed text-stone-300">
                Pas de surprise : un setup fixe, un abonnement fixe quelle que
                soit votre utilisation.
              </p>
            </article>
          </div>
        </section>

        <section className="mt-20">
          <h2 className="font-heading text-2xl font-semibold text-[#010101]">
            📅 Plans mensuels (sans engagement)
          </h2>
          <div className="mt-6 overflow-x-auto rounded-2xl border-2 border-[#010101]/15 bg-white shadow-md">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm relative">
              <colgroup>
                <col />
                <col />
                <col className="bg-[#A43032]/5 border-x-2 border-[#A43032]" />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr className="border-b-2 border-[#A43032] bg-[#010101] text-white">
                  <th
                    className="px-4 py-3 text-left font-semibold text-white"
                    scope="col"
                  />
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Starter
                  </th>
                  <th
                    className="px-4 py-3 font-bold text-white bg-[#A43032] border-x-2 border-[#A43032]"
                    scope="col"
                  >
                    <div className="flex items-center gap-2">
                      Business
                      <span className="whitespace-nowrap rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white shadow-sm">
                        Recommandé
                      </span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Pro
                  </th>
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Sur mesure
                  </th>
                </tr>
              </thead>
              <tbody className="text-stone-800">
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Cible
                  </td>
                  <td className="px-4 py-3">TPE 1 équipe</td>
                  <td className="px-4 py-3">PME — plusieurs équipes</td>
                  <td className="px-4 py-3">PME / ETI</td>
                  <td className="px-4 py-3">Besoin spécifique</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Setup (1<sup>ère</sup> facture)
                  </td>
                  <td className="px-4 py-3"><span className="font-mono">250 €</span></td>
                  <td className="px-4 py-3"><span className="font-mono">500 €</span></td>
                  <td className="px-4 py-3"><span className="font-mono">1 000 €</span></td>
                  <td className="px-4 py-3">Sur devis</td>
                </tr>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <td className="px-4 py-3 font-semibold text-[#010101]">
                    Abonnement mensuel
                  </td>
                  <td className="px-4 py-3 font-bold"><span className="font-mono font-bold">90 €/mois</span></td>
                  <td className="px-4 py-3 font-black text-[#A43032] text-base">
                    <span className="font-mono font-bold">150 €/mois</span>
                  </td>
                  <td className="px-4 py-3 font-bold"><span className="font-mono font-bold">320 €/mois</span></td>
                  <td className="px-4 py-3 font-bold">Sur devis</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Sous-clients (départements)
                  </td>
                  <td className="px-4 py-3">1</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">Illimité</td>
                  <td className="px-4 py-3">Illimité</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Utilisateurs chatbot
                  </td>
                  <td className="px-4 py-3">5</td>
                  <td className="px-4 py-3">20</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">Illimité</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Stockage documents
                  </td>
                  <td className="px-4 py-3">500 Mo</td>
                  <td className="px-4 py-3">3 Go</td>
                  <td className="px-4 py-3">15 Go</td>
                  <td className="px-4 py-3">Illimité</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    White-label (couleurs, logo)
                  </td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    OCR (PDF scannés, images)
                  </td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Portail self-service client
                  </td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">✅</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Modèle
                  </td>
                  <td className="px-4 py-3">Basique</td>
                  <td className="px-4 py-3">Medium</td>
                  <td className="px-4 py-3">Large</td>
                  <td className="px-4 py-3">À définir</td>
                </tr>
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Prompt système personnalisé
                  </td>
                  <td className="px-4 py-3">Prompt de base</td>
                  <td className="px-4 py-3">✅</td>
                  <td className="px-4 py-3">Par sous-client</td>
                  <td className="px-4 py-3">Sur mesure</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Support
                  </td>
                  <td className="px-4 py-3">E-mail 5 j/7</td>
                  <td className="px-4 py-3">E-mail prioritaire</td>
                  <td className="px-4 py-3">Téléphone + SLA 48 h</td>
                  <td className="px-4 py-3">Sur mesure</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-20">
          <h2 className="font-heading text-2xl font-semibold text-[#010101]">
            📆 Engagement 12 mois (−20 %)
          </h2>
          <p className="mt-2 text-sm font-medium text-stone-800">
            Réduction sur l’abonnement et le setup par rapport aux tarifs sans
            engagement.
          </p>
          <div className="mt-6 overflow-x-auto rounded-2xl border-2 border-[#010101]/15 bg-white shadow-md">
            <table className="w-full min-w-[520px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b-2 border-[#A43032] bg-[#010101] text-white">
                  <th
                    className="px-4 py-3 text-left font-semibold text-white"
                    scope="col"
                  />
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Starter
                  </th>
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Business
                  </th>
                  <th
                    className="px-4 py-3 font-bold text-[#A43032]"
                    scope="col"
                  >
                    Pro
                  </th>
                </tr>
              </thead>
              <tbody className="text-stone-800">
                <tr className="border-b border-stone-100">
                  <td className="px-4 py-3 font-medium text-[#010101]">
                    Setup
                  </td>
                  <td className="px-4 py-3"><span className="font-mono">190 €</span></td>
                  <td className="px-4 py-3"><span className="font-mono">350 €</span></td>
                  <td className="px-4 py-3"><span className="font-mono">800 €</span></td>
                </tr>
                <tr className="bg-[#A43032]/5">
                  <td className="px-4 py-3 font-semibold text-[#010101]">
                    Abonnement
                  </td>
                  <td className="px-4 py-3 font-semibold"><span className="font-mono font-bold">75 €/mois</span></td>
                  <td className="px-4 py-3 font-semibold"><span className="font-mono font-bold">128 €/mois</span></td>
                  <td className="px-4 py-3 font-semibold"><span className="font-mono font-bold">250 €/mois</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16 rounded-2xl border-2 border-dashed border-[#A43032] bg-[#f7f4f4] p-6 shadow-sm">
          <h2 className="font-heading text-xl font-semibold text-[#010101]">
            🤝 Parrainage
          </h2>
          <p className="mt-2 text-stone-800">
            <strong className="text-[#010101]">−10 €</strong> par mois pendant
            la durée du parrain, jusqu’à{" "}
            <strong className="text-[#010101]">3</strong> parrains actifs.
          </p>
        </section>

        <div className="mt-16 flex flex-wrap gap-3">
          <Button asChild variant="brand" size="lg">
            <Link
              to="/contact"
              onClick={() =>
                trackEvent("Abonnement", "Clic_Demander_Devis", "Tarifs")
              }
            >
              Demander un devis <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/">Retour accueil</Link>
          </Button>
        </div>
      </main>
    </>
  );
}
